import { ZfFilesUpload, ZfIconButton, ZfLoadingSpinner, ZfPopover, ZfPopoverFooter, ZfTextButton } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch } from 'react-router';
import { useAddGenericDocuments, useClientPackages, useCreateClientPackage } from '../../api';
import type { EnrichedClientDto } from '../../api/generated/client';
import { CLIENT_PACKAGE_DOCUMENT_TYPE } from '../../config/constants';
import { useWealthPlanningNavigate } from '../../hooks/useWealthPlanningNavigate';
import { wnSendTrace } from '../../utils/wn-event-utils';
import { DocumentList } from './DocumentList';
import { DocumentListItem } from './DocumentListItem';

interface IProps {
    client: EnrichedClientDto;
}

export const DocumentCounter: FunctionComponent<IProps> = ({ client }) => {
    const { t } = useTranslation();
    const { basePath, navigate } = useWealthPlanningNavigate();

    const { draftPackage, isLoading } = useClientPackages(client);
    const packageIdRoute = draftPackage != null ? `/${draftPackage.id}` : '';
    const createClientPackage = useCreateClientPackage();
    const addGenericDocuments = useAddGenericDocuments(client.id);

    const documentsRouteMatch = useMatch(`${basePath}/${client.partnerUuid}/documents${packageIdRoute}`);

    const additionalDocuments =
        draftPackage != null
            ? [...draftPackage.clientPackageAppendixDocuments].filter((appendix) => appendix.type === 'OTHER').sort((a, b) => a.documentId - b.documentId)
            : [];
    const clientPackageDocuments = draftPackage?.clientPackageDocuments ?? [];
    const selectedDocuments = [...clientPackageDocuments, ...additionalDocuments];

    const onUploadDocuments = (event: CustomEvent<File[]>) => {
        addGenericDocuments.mutateAsync({ packageId: draftPackage!.id, files: event.detail });
    };

    const navigateToDocuments = useCallback(
        (showDraftPackage: boolean, packageId?: number) => {
            const packageIdPath = packageId ? `/${packageId}` : packageIdRoute;
            navigate(`/${client.partnerUuid}/documents${showDraftPackage ? packageIdPath : ''}`);
        },
        [client.partnerUuid, packageIdRoute, navigate],
    );

    const onClientPackageProceed = useCallback(async () => {
        if (draftPackage == null) {
            const newPackage = await createClientPackage.mutateAsync(client.id);
            navigateToDocuments(true, newPackage.id);
            wnSendTrace({
                feature: 'client-package',
                tags: {
                    partnerUuid: client.partnerUuid,
                    action: 'create-new-client-package',
                    source: 'document-handler-popup',
                    newPackageId: `${newPackage.id}`,
                },
            });
        } else if (!documentsRouteMatch) {
            navigateToDocuments(true);
            wnSendTrace({
                feature: 'client-package',
                tags: {
                    partnerUuid: client.partnerUuid,
                    action: 'finalize-client-package-click',
                    source: 'document-handler-popup',
                    draftPackageId: `${draftPackage.id}`,
                },
            });
        }
    }, [draftPackage, documentsRouteMatch, client.id, client.partnerUuid, createClientPackage, navigateToDocuments]);

    const onClientPackageViewAll = () => {
        navigateToDocuments(false);
        wnSendTrace({
            feature: 'client-package',
            tags: {
                partnerUuid: client.partnerUuid,
                action: 'view-all-client-packages-click',
                source: 'document-handler-popup',
            },
        });
    };

    return (
        <div style={{ position: 'relative' }}>
            <ZfPopover side="bottom" alignment="end">
                {isLoading || createClientPackage.isPending ? (
                    <ZfLoadingSpinner />
                ) : (
                    <>
                        <ZfIconButton slot="anchor" icon="folder" size="medium" hierarchy="tertiary" />
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '215px',
                                marginBottom: 'var(--padding-bottom-xs)',
                            }}
                        >
                            <DocumentList
                                clientId={client.id}
                                packageId={draftPackage?.id}
                                hasDraftPackage={draftPackage != null}
                                hasDocs={!!selectedDocuments?.length}
                                clientPackageDocuments={clientPackageDocuments}
                                additionalDocuments={additionalDocuments}
                            />
                            {draftPackage != null && (
                                <DocumentListItem>
                                    <ZfFilesUpload
                                        text={t('clientPackageAppendixActionUpload')}
                                        size="extra-small"
                                        hierarchy="tertiary"
                                        accept={CLIENT_PACKAGE_DOCUMENT_TYPE}
                                        onFileInputChange={onUploadDocuments}
                                    />
                                </DocumentListItem>
                            )}
                        </div>
                        <ZfPopoverFooter>
                            <ZfTextButton hierarchy="tertiary" size="extra-small" onClick={onClientPackageViewAll}>
                                {t('viewAll')}
                            </ZfTextButton>
                            <ZfTextButton size="extra-small" onClick={onClientPackageProceed}>
                                {t(draftPackage != null ? 'clientPackageFinalise' : 'clientPackageCreate')}
                            </ZfTextButton>
                        </ZfPopoverFooter>
                    </>
                )}
            </ZfPopover>
            {selectedDocuments?.length ? (
                <div className="toolbar__document--badge">
                    <span>{selectedDocuments.length}</span>
                </div>
            ) : null}
        </div>
    );
};
